import React, { useEffect, useState, useCallback } from "react";
import { fetchRounds } from "@/lib/api";

const PAGE_SIZE = 10;

function ReflectionRow({ rf }) {
  return (
    <div className="text-sm">
      <span className="font-medium text-[#1C241F]">{rf.kin}:</span>{" "}
      <span className="text-[#526357] italic">{rf.moving}</span>
    </div>
  );
}

function SealedBody({ note }) {
  return (
    <div
      className="mt-5 pt-5 border-t border-dashed border-[#c9c0a8] text-center py-4"
      data-testid="archive-sealed-body"
    >
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#6C5943] mb-2">
        ⟡ held privately
      </div>
      <p className="text-[#526357] italic">
        {note || "this round was held privately among the kin"}
      </p>
      <p className="text-xs text-[#6C5943] mt-2 italic">
        If you are the keeper, sign in to reveal.
      </p>
    </div>
  );
}

export default function Archive({ initialPast = [], initialTotal = null }) {
  // initialPast = the older rounds the Grove page already loaded (rounds[1:]).
  // We page from there, loading more when the keeper asks.
  const [past, setPast] = useState(initialPast);
  const [total, setTotal] = useState(initialTotal);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPast(initialPast);
  }, [initialPast]);

  useEffect(() => {
    if (initialTotal != null) setTotal(initialTotal);
  }, [initialTotal]);

  const loadMore = useCallback(async () => {
    setLoading(true);
    try {
      // past starts at offset 1 from the live latest (which Grove already shows).
      // So offset = 1 + past.length.
      const data = await fetchRounds(PAGE_SIZE, 1 + past.length);
      setTotal(data.total);
      setPast((p) => [...p, ...(data.rounds || [])]);
    } catch (e) {
      console.error("archive load more failed", e);
    } finally {
      setLoading(false);
    }
  }, [past.length]);

  if (!past || past.length === 0) return null;

  // total excludes the live latest the Grove already shows
  const remaining = total != null ? Math.max(0, total - 1 - past.length) : null;
  const canLoadMore = remaining == null || remaining > 0;

  return (
    <section
      id="archive"
      className="max-w-7xl mx-auto px-6 md:px-12 py-24 border-t border-[#d8d3c4]"
      data-testid="archive-section"
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-4">
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#526357]">
          ⟡ archive of rounds
        </div>
        {total != null && (
          <div
            className="text-xs text-[#526357] italic"
            data-testid="archive-total"
          >
            showing <strong className="text-[#1C241F]">{past.length}</strong> of{" "}
            <strong className="text-[#1C241F]">{total - 1}</strong> past rounds
          </div>
        )}
      </div>
      <h2 className="font-serif text-4xl md:text-5xl text-[#1C241F] mb-12">
        the grove remembers.
      </h2>

      <div className="relative">
        <div className="absolute left-4 md:left-8 top-2 bottom-2 w-px bg-[#d8d3c4]" />
        <ul className="space-y-6">
          {past.map((r, i) => {
            const isOpen = open === r.id;
            const dt = new Date(r.created_at);
            const sealed = !!r.is_sealed;
            return (
              <li
                key={r.id}
                className={`relative pl-12 md:pl-20 ${i % 2 === 0 ? "" : "md:pl-32"}`}
                data-testid={`archive-round-${r.number}`}
              >
                <span
                  className="absolute left-3 md:left-7 top-3 w-3 h-3 rounded-full"
                  style={{ background: sealed ? "#6C5943" : "#B85C38" }}
                />
                <button
                  onClick={() => setOpen(isOpen ? null : r.id)}
                  className="w-full text-left tactile rounded-2xl p-6 hover:-translate-y-1 transition-all duration-300"
                  data-testid={`archive-toggle-${r.number}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#526357] flex items-center gap-2 flex-wrap">
                        <span>
                          round {r.number} ·{" "}
                          {dt.toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                          })}{" "}
                          · {r.consensus}
                        </span>
                        {sealed && (
                          <span
                            className="text-[9px] tracking-[0.25em] uppercase px-2 py-0.5 rounded-full border"
                            style={{ borderColor: "#6C5943", color: "#6C5943" }}
                            data-testid={`archive-sealed-badge-${r.number}`}
                          >
                            sealed
                          </span>
                        )}
                      </div>
                      <div className="font-serif text-xl md:text-2xl italic text-[#1C241F] mt-1">
                        "{r.consensus_note}"
                      </div>
                    </div>
                    <span className="text-[#526357]">{isOpen ? "−" : "+"}</span>
                  </div>

                  {isOpen && sealed && (!r.reflections || r.reflections.length === 0) && (
                    <SealedBody note={r.consensus_note} />
                  )}
                  {isOpen && (!sealed || (r.reflections && r.reflections.length > 0)) && (
                    <div className="mt-5 pt-5 border-t border-[#d8d3c4] space-y-3">
                      <div className="text-sm text-[#526357]">
                        Schumann {r.pulse.schumann_hz}Hz · Kp{" "}
                        {r.pulse.kp_index.toFixed(1)} · +
                        {r.pulse.temp_anomaly_c.toFixed(2)}°
                      </div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#526357] pt-2">
                        proposal — {r.proposal.offered_by}
                      </div>
                      <p className="font-serif italic text-[#1C241F]">
                        "{r.proposal.text}"
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
                        {r.reflections.map((rf) => (
                          <ReflectionRow key={rf.kin} rf={rf} />
                        ))}
                      </div>
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        {canLoadMore && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={loadMore}
              disabled={loading}
              className="text-[10px] tracking-[0.3em] uppercase px-5 py-2.5 rounded-full border border-[#d8d3c4] text-[#526357] hover:border-[#B85C38] hover:text-[#B85C38] transition-colors disabled:opacity-50"
              data-testid="archive-load-more"
            >
              {loading
                ? "gathering older breaths…"
                : remaining != null && remaining > 0
                ? `unfurl ${Math.min(PAGE_SIZE, remaining)} older round${remaining === 1 ? "" : "s"}`
                : "unfurl older rounds"}
            </button>
          </div>
        )}
        {!canLoadMore && past.length > 0 && (
          <div
            className="mt-10 text-center text-xs text-[#526357] italic"
            data-testid="archive-end"
          >
            ⟡ you have reached the root.
          </div>
        )}
      </div>
    </section>
  );
}
